import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { statusesSettings } from "../../constants/status"
import { useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { useRoadworkUpdate } from "../../hooks/mutations/roadwork/useRoadworkUpdate"
import { useRoadworkWithTasks } from "../../hooks/queries/useRoadwork"

import * as Styled from "./StatusSelector.styled"

export function StatusSelector(props) {

    const { className, roadworkUuid } = props
    const { hasPendingChange, isEditingRoadwork } = useRoadworksStateContext()
    const readOnly = useRoleIsReadOnly()
    const isMobile = useIsMobileDevice()

    const { road } = useParams()
    const { data: roadwork } = useRoadworkWithTasks(roadworkUuid, road)

    const displayedStatuses = useMemo(() => {
        if (!roadwork) {
            return []
        }
        return statusesSettings[roadwork.status].allowedStatusTransitions?.[roadwork.isLogiroadRecommendationIso ? "iso" : "notIso"](roadwork) || []
    }, [roadwork])

    const canChangeStatus = useMemo(() => {
        const currentYear = new Date().getFullYear()
        return roadwork?.year >= currentYear && !readOnly && displayedStatuses.length > 0
    }, [displayedStatuses, readOnly, roadwork])

    const [anchorEl, setAnchorEl] = useState(null)
    const open = !!anchorEl

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const { t } = useTranslation(I18N_NAMESPACE)

    const mutation = useRoadworkUpdate(roadwork, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.statusSelector.snackbar.error"),
        successSnackbarDuration: 10000,
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.statusSelector.snackbar.success"),
    })

    const handleStatusChange = useCallback(async (newStatus) => {
        mutation.mutate({
            ...roadwork,
            status: newStatus,
        })
        handleClose()
    }, [mutation, roadwork])

    const isSelectorDisabled = isEditingRoadwork || hasPendingChange || !canChangeStatus

    if (!roadwork) {
        return null
    }

    return (
        <Styled.SelectorButtonContainer className={className} id="status-selector-container" isMobile={isMobile}>
            <Styled.SelectorButton
                disabled={isSelectorDisabled}
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
            >
                {t(I18N_NAMESPACE, statusesSettings[roadwork.status].statusLabel)} {!isSelectorDisabled ? <Styled.ArrowDownIcon /> : null}
            </Styled.SelectorButton>
            <Styled.Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    list: {
                        "aria-labelledby": "basic-button",
                    },
                }}
            >
                {displayedStatuses.map(({ status, icon }) => {
                    return <Styled.MenuItem
                        onClick={() => handleStatusChange(status)}
                        key={status}>
                        {icon} {`${t(I18N_NAMESPACE, statusesSettings[roadwork.status].statusLabel)} > ${t(I18N_NAMESPACE, statusesSettings[status].statusLabel)}`}
                    </Styled.MenuItem>
                })}
            </Styled.Menu>
        </Styled.SelectorButtonContainer>)
}